import React, { useCallback, useEffect, useState } from 'react';

import RangeSlider from '../../../../library/range-slider/range-slider';
import CategoryCollapsible from '../category-collapsible/category-collapsible';
import { getLimitedValue } from './price-range-category-utils';

import './price-range-category.scss';

function isNewValueDifferent(proposedNewValue, value) {
  return (
    proposedNewValue?.[0] !== value?.[0] || proposedNewValue?.[1] !== value?.[1]
  );
}

interface PriceRangeCategoryProps {
  categoryCode?: string;
  categoryName?: string;
  defaultMaxPrice?: number;
  defaultMinPrice?: number;
  description?: string;
  highestPriceInResults?: number;
  isLoadingPLP?: boolean;
  isOpenByDefault?: boolean;
  lowestPriceInResults?: number;
  onApply: (value) => void;
  onSliderValueChange: (value) => void;
  toggleType?: string;
}

function PriceRangeCategory(props: PriceRangeCategoryProps) {
  const {
    categoryCode = '',
    categoryName = '',
    description,
    isOpenByDefault = false,
    isLoadingPLP,
    defaultMaxPrice,
    defaultMinPrice,
    highestPriceInResults,
    lowestPriceInResults,
    onApply,
    onSliderValueChange,
    toggleType
  } = props;

  const [value, setValue] = useState([
    defaultMinPrice || 0,
    defaultMaxPrice || 0
  ]);
  const [stateMin, stateMax] = value;

  function applyRange([min, max]: number[]) {
    onApply({
      categoryCode,
      categoryName,
      type: 'slider',
      valueCode: `[${min} TO ${max}]`,
      valueName: `$${min} - $${max}`
    });
  }

  function onAfterChange() {
    applyRange(value);
    onSliderValueChange(value);
  }

  const onSliderChange = useCallback(
    newValues => {
      const proposedNewValue = getLimitedValue(
        newValues,
        lowestPriceInResults,
        highestPriceInResults,
        defaultMinPrice,
        defaultMaxPrice
      );
      if (isNewValueDifferent(proposedNewValue, value)) {
        setValue(proposedNewValue);
      }
    },
    [
      defaultMaxPrice,
      defaultMinPrice,
      highestPriceInResults,
      lowestPriceInResults,
      value
    ]
  );

  useEffect(() => {
    const proposedNewValue = getLimitedValue(
      value,
      lowestPriceInResults,
      highestPriceInResults,
      defaultMinPrice,
      defaultMaxPrice
    );
    if (!isLoadingPLP && isNewValueDifferent(proposedNewValue, value)) {
      setValue(proposedNewValue);
    }
  }, [
    defaultMaxPrice,
    defaultMinPrice,
    highestPriceInResults,
    isLoadingPLP,
    lowestPriceInResults,
    value
  ]);

  return (
    <CategoryCollapsible
      description={description}
      isOpenByDefault={isOpenByDefault}
      label={categoryName}
      toggleType={toggleType}
    >
      <div styleName="price-range-container">
        <span styleName="price-range">
          $ {stateMin}
          <span styleName="price-range-filter-to-label"> to </span> ${stateMax}
        </span>

        <div styleName="slider-container">
          <RangeSlider
            max={Math.max(highestPriceInResults || 0, 0)}
            min={Math.max(lowestPriceInResults || 0, 0)}
            onAfterChange={onAfterChange}
            onChange={onSliderChange}
            values={value}
          />
        </div>
      </div>
    </CategoryCollapsible>
  );
}

export default PriceRangeCategory;
